/* Delete-confirmation modal for a Pipeline lead.
 *
 * Extracted from `pages/app/Pipeline.jsx` (Jun 2026). Renders null
 * when `lead` is falsy so the parent can keep using
 * `{deleteTarget && <DeleteLeadModal ... />}` semantics without
 * worrying about mount/unmount churn.
 *
 * The modal offers TWO outcomes for the same misclick-prone gesture:
 *   1. "Move to Trash" — calls the parent's `onConfirm` which DELETEs
 *      the lead (30-day soft-delete grace window).
 *   2. "Move to Dead instead" — calls the parent's `onMoveToDead`
 *      which PATCHes the stage to Dead. Preserves the lead +
 *      history but removes it from the active funnel.
 *
 * Loading state (`deleting`) disables ALL buttons + the backdrop
 * dismiss so the user can't double-fire mid-network.
 */
import { Trash2, Loader2 } from "lucide-react";

export default function DeleteLeadModal({
  lead,
  deleting,
  onCancel,
  onConfirm,
  onMoveToDead,
}) {
  if (!lead) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={() => !deleting && onCancel()}
      data-testid="pipeline-delete-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
            <Trash2 size={18} />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg text-[var(--ink)] leading-tight">
              Delete this lead?
            </h3>
            <p className="text-sm text-[var(--ink-2)] mt-1">
              <strong>{lead.property_address || "This lead"}</strong>
              {" "}will move to Trash. You can restore it within 30 days from{" "}
              <strong>Leads → Trash</strong>; after that it&apos;s permanently purged.
            </p>
          </div>
        </div>

        <div className="text-xs text-[var(--ink-3)] bg-[var(--bg)] border border-[var(--line)] rounded-lg p-3 mb-5">
          <strong className="text-[var(--ink-1)]">Tip:</strong> If you want to keep the lead but
          flag it as dead, use <strong>Move to Dead instead</strong> — it preserves history in the
          Dead column without sending it to Trash.
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-[var(--ink-1)] hover:bg-[var(--bg)] transition disabled:opacity-50"
            data-testid="pipeline-delete-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onMoveToDead}
            disabled={deleting || lead.stage === "Dead"}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-[var(--line)] text-[var(--ink-1)] hover:bg-slate-50 transition disabled:opacity-50"
            data-testid="pipeline-delete-move-to-dead"
          >
            {deleting ? <Loader2 size={14} className="animate-spin" /> : null}
            Move to Dead instead
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-rose-600 hover:bg-rose-500 text-white transition disabled:opacity-50"
            data-testid="pipeline-delete-confirm"
          >
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Move to Trash
          </button>
        </div>
      </div>
    </div>
  );
}
